import { IntegrationApiFeaturesCongestionReportsContractsDataUnderlyingAsset } from '@alliander-fe/api';

import { capitalize } from 'src/utils/capitalize';

import { FilteredCongestionReportsParagraphProps } from 'components/CongestionReportsParagraph';
import { commaSeparatedStringFormatter } from '../../../utils/commaSeparatedStringFormatter';

type helperCausesTextProps = {
  cause?: IntegrationApiFeaturesCongestionReportsContractsDataUnderlyingAsset[];
} & Pick<
  FilteredCongestionReportsParagraphProps,
  'causeCapacityText' | 'causeGridSecurityText' | 'causeVoltageText'
>;

/**
 * This function returns the causes of the congestion report.
 * @param cause - The causes of the congestion report.
 * @param rest - The rest of the props.
 * @returns The causes of the congestion report.
 */
export const getCausesText = ({ cause, ...rest }: helperCausesTextProps) => {
  if (!cause) return null;

  const currentCauses = cause.map((cause) => cause.cause);
  const flattenAvailableCauses = currentCauses.toString().replace(/\s/g, '').split(',');
  const uniqueCauses = [...new Set(flattenAvailableCauses)];

  const availableCauseTexts = uniqueCauses
    .map((cause) => mapCauseTexts({ cause, ...rest }))
    .filter((causeText) => causeText !== null);

  if (!availableCauseTexts.length) return null;

  return stringArrayToRichText(availableCauseTexts);
};

type helperAvailableCauseTextsProps = {
  cause: string | null | undefined;
} & Pick<
  FilteredCongestionReportsParagraphProps,
  'causeCapacityText' | 'causeGridSecurityText' | 'causeVoltageText'
>;

/**
 * This function returns the cause text for the given cause.
 * @param cause - The cause of the congestion report.
 * @gitparam rest - The rest of the props.
 * @returns The cause text for the given cause.
 */
const mapCauseTexts = ({ cause, ...rest }: helperAvailableCauseTextsProps) => {
  if (!cause) return null;

  switch (cause.toLowerCase()) {
    case 'capaciteit':
      return rest.causeCapacityText?.toString();
    case 'spanning':
      return rest.causeVoltageText?.toString();
    case 'netveiligheid':
      return rest.causeGridSecurityText?.toString();
    default:
      return null;
  }
};

const stringArrayToRichText = (results: string[]) => {
  if (results.length <= 1) {
    return `${commaSeparatedStringFormatter(results.join(', '))}.`;
  }

  const listItems = results.map((result) => `<li>${capitalize(result)}.</li>`).join('');

  return `:<ul>${listItems}</ul>`;
};
